import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Button, Empty, Select, message } from "antd";
import { useApprovalData } from "../../../utils/hook/useApprovalData";
import { ApprovalModal } from "../../../components/modal/ApprovalModal";
import ButtonComp from "../../../components/button/ButtonComp";
import ApprovalTimeline from "../../../components/Contract/ApprovalTimeline";
const { Option } = Select;

const ApprovalMatrixTimeline2 = ({ contract }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [approvalMode, setApprovalMode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const currentUser = useSelector((store) => store.auth.userInfo.name);
    const comp_id = useSelector((store) => store.auth.userInfo.company_id);
    const userRole = useSelector((store) => store.auth.userInfo.role || "");
    const isRestrictedRole = ["business", "admin"].some((r) =>
        userRole.toLowerCase().includes(r)
    );
    const editableUsername = contract.editable_username; 
    const canEdit = !isRestrictedRole && currentUser === editableUsername;
    const company_id = Number(localStorage.getItem("companyId")) || comp_id;

    const { allUsers, setAllUsers, statusUsers, setStatusUsers, fetchData } =
        useApprovalData(contract, company_id);

    useEffect(() => {
        fetchData();
        setEditingUserId(null);
    }, [contract.id, company_id]);

    const toggleSelection = (user) => {
        const key = user.approval_id ?? user.id;
        if (user.status === "approved" || user.status === "rejected") return;
        setSelectedUsers((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const sendApprovalFromModal = async () => {
        if (isRestrictedRole) return;
        const selectedIds = selectedUsers.filter((k) =>
            allUsers.some((u) => u.id === k)
        );
        if (!selectedIds.length)
            return message.error("Please select at least one user from modal.");

        const usersToSend = allUsers.filter((u) => selectedIds.includes(u.id));
        const payload = {
            company_id,
            agreement_id: contract.id,
            approval_mode: "draft",
            users: usersToSend.map((u) => ({
                user_id: u.id,
                scheduled_date: u.scheduled_date
                    ? dayjs(u.scheduled_date).format("YYYY-MM-DDTHH:mm:ss")
                    : null,
                remarks: u.remarks || "",
                agreement_status: u.scheduled_date ? null : u.agreement_status || null,
            })),
        };

        try {
            setLoading(true);
            console.log("📤 Modal Send Payload:", payload);
            await axiosInstance.post("/contract/send-approval-emails/", payload);
            message.success("Approval requests sent (modal).");
            await fetchData();
            setSelectedUsers([]);
            setModalOpen(false);
        } catch (err) {
            console.error(err);
            message.error("Failed to send approval requests from modal.");
        } finally {
            setLoading(false);
        }
    };

    const sendApprovalFromTimeline = async () => {
        if (isRestrictedRole) return;

        const timelineUsers = selectedUsers
            .map((k) => statusUsers.find((s) => s.approval_id === k))
            .filter(Boolean);

        if (!timelineUsers.length)
            return message.error("Please select at least one timeline user.");

        try {
            setLoading(true);

            const draftUsers = timelineUsers.filter((u) => u.status === "draft");
            if (draftUsers.length) {
                const payload = {
                    company_id,
                    agreement_id: contract.id,
                    approval_mode: approvalMode || "all",
                    users: draftUsers.map((u) => ({
                        user_id: u.id,
                        scheduled_date: u.scheduled_date
                            ? dayjs(u.scheduled_date).format("YYYY-MM-DDTHH:mm:ss")
                            : null,
                        remarks: u.remarks || "",
                        agreement_status:
                            u.agreement_status ,
                    })),
                };
                console.log("📤 Draft Send Payload:", payload);
                await axiosInstance.post("/contract/send-approval-emails/", payload);
            }

            const pendingUsers = timelineUsers.filter((u) => u.status === "pending");
            for (const u of pendingUsers) {
                const payload = {
                    approval_id: u.approval_id,
                    user_id: u.id,
                    company_id,
                    approval_mode: approvalMode || "all",
                    scheduled_date: u.scheduled_date
                        ? dayjs(u.scheduled_date).format("YYYY-MM-DDTHH:mm:ss")
                        : null,
                    remarks: u.remarks || "",
                    status: "Sent for approval",
                    agreement_status:
                        u.agreement_status ,
                };
                console.log("📤 Pending Resend Payload:", payload);
                await axiosInstance.post("/contract/resend-approval-email/", payload);
            }

            message.success("Approval requests processed successfully.");
            await fetchData();
            setSelectedUsers([]);
        } catch (err) {
            console.error(err);
            message.error("Failed to send/resend approval requests.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 py-4 px-2">
            {!canEdit && (
                <h1 className="font-semibold text-xl text-gray-800 dark:text-white pb-4">
                    Approval Timeline
                </h1>
            )}

            {canEdit && (
                <div>
                    <div className="flex items-center justify-between ">
                        <h1 className="font-semibold text-xl text-gray-800 dark:text-white">
                            Approval Timeline
                        </h1>
                        <ButtonComp
                            text="Set Approval Matrix"
                            textColor="#4E36A3"
                            onClick={() => setModalOpen(true)}
                        />
                    </div>

                    <ApprovalModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        allUsers={allUsers}
                        setAllUsers={setAllUsers}
                        selectedUsers={selectedUsers}
                        toggleSelection={toggleSelection}
                        approvalMode={approvalMode}
                        setApprovalMode={setApprovalMode}
                        handleSend={sendApprovalFromModal}
                        loading={loading}
                        isRestrictedRole={isRestrictedRole}
                    />

                    {statusUsers.length > 0 && <div className="flex items-center justify-between gap-3 my-4 bg-gray-50 dark:bg-[#0E0E2C] p-3 rounded-lg shadow-sm">
                        <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-700 dark:text-gray-200">
                                Approval Mode:
                            </span>
                            <Select
                                value={approvalMode || undefined}
                                placeholder="Select Mode"
                                onChange={(val) => setApprovalMode(val)}
                                style={{ width: 180 }}
                                size="middle"
                            >
                                <Option value="serial">Sequential</Option>
                                <Option value="all">Parallel (All)</Option>
                            </Select>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                type="default"
                                onClick={sendApprovalFromTimeline}
                                loading={loading}
                            >
                                Send Approval Email
                            </Button>
                        </div>
                    </div>}
                </div>
            )}

            {statusUsers.length > 0 ? statusUsers.map((user) => (
                <div className="border-l-2 border-gray-300 dark:border-gray-600 relative ml-2 pb-4 !mt-[0px] pl-4">
                    <ApprovalTimeline
                        key={user.approval_id ?? user.id}
                        user={user}
                        company_id={company_id}
                        fetchData={fetchData}
                        loading={loading}
                        setLoading={setLoading}
                        selectedUsers={selectedUsers}
                        setSelectedUsers={setSelectedUsers}
                        statusUsers={statusUsers}
                        setStatusUsers={setStatusUsers}
                        editingUserId={editingUserId}
                        setEditingUserId={setEditingUserId}
                        approvalMode={approvalMode}
                        isRestrictedRole={isRestrictedRole}
                        canEdit={canEdit}
                    />
                </div>
            ))
                : <p className="h-[60vh] flex justify-center items-center">
                    <Empty />
                </p>}
        </div>
    );
};

export default ApprovalMatrixTimeline2;
